import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { AlertCircle, RefreshCw } from 'lucide-react';

export function UpdateNotification(_props: { className?: string }) {
  // Disable UI in production for silent updates
  const isDev = process.env.NODE_ENV !== 'production';
  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    if (!isDev) return; // no-op in production

    // Listen for service worker update events (dev only)
    const handleUpdateFound = () => {
      setUpdateAvailable(true);
    };

    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistration().then(registration => {
        if (registration && registration.waiting) {
          setUpdateAvailable(true);
        }
      });
    }

    window.addEventListener('swUpdateAvailable', handleUpdateFound);

    return () => {
      window.removeEventListener('swUpdateAvailable', handleUpdateFound);
    };
  }, [isDev]);

  const handleUpdate = () => {
    if (window.updateServiceWorker) {
      window.updateServiceWorker();
    } else {
      window.location.reload();
    }
  };

  if (!isDev || !updateAvailable) return null;

  return (
    <>
      {/* Semi-transparent overlay */}
      <div className="fixed inset-0 bg-black/50 z-[9999]" />

      {/* Update notification dialog */}
      <div className="fixed inset-0 flex items-center justify-center z-[10000] pb-20 md:pb-0 ios-header-padding">
        <div className="bg-white rounded-lg shadow-xl border border-gray-200 p-6 max-w-sm w-full mx-4 animate-in fade-in-0 zoom-in-95">
          <div className="flex flex-col items-center text-center">
            <div className="h-12 w-12 rounded-full bg-blue-100 flex items-center justify-center mb-4">
              <AlertCircle className="h-7 w-7 text-blue-600" />
            </div>

            <h2 className="text-xl font-semibold text-gray-900 mb-2">App Update Available</h2>

            <p className="text-gray-600 mb-6">
              A new version of the app is available. Please update now to get the latest features and improvements.
            </p>

            <Button
              onClick={handleUpdate}
              size="lg"
              className="bg-blue-600 hover:bg-blue-700 text-white w-full"
            >
              <RefreshCw className="h-5 w-5 mr-2" />
              Update Now
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
